import { supabase } from '@/lib/supabase/client';
import type {
  PurchaseOrderItem,
  PurchaseOrderItemInsert,
  PurchaseOrderItemUpdate,
} from '@/types/purchase-order-items';

export const purchaseOrderItemsService = {
  async getByPurchaseOrderId(purchaseOrderId: string, strict = false): Promise<PurchaseOrderItem[]> {
    try {
      const { data, error } = await supabase
        .from('purchase_order_items')
        .select('*')
        .eq('purchase_order_id', purchaseOrderId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching purchase order items:', error);
        if (strict) throw error;
        return [];
      }

      return (data as PurchaseOrderItem[]) || [];
    } catch (error) {
      console.error('Error fetching purchase order items:', error);
        if (strict) throw error;
      return [];
    }
  },

  async getById(id: string): Promise<PurchaseOrderItem | null> {
    try {
      const { data, error } = await supabase
        .from('purchase_order_items')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching purchase order item:', error);
        return null;
      }

      return (data as PurchaseOrderItem) || null;
    } catch (error) {
      console.error('Error fetching purchase order item:', error);
      return null;
    }
  },

  async create(data: PurchaseOrderItemInsert): Promise<PurchaseOrderItem> {
    try {
      const insertData = {
        purchase_order_id: data.purchase_order_id,
        component_id: data.component_id ?? null,
        item_name: data.item_name,
        item_sku: data.item_sku ?? null,
        vendor: data.vendor ?? null,
        quantity: data.quantity,
        unit_cost: data.unit_cost,
        total_cost: data.total_cost,
        description: data.description ?? null,
      };

      const { data: newItem, error } = await supabase
        .from('purchase_order_items')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating purchase order item:', error);
        throw new Error(`Failed to create purchase order item: ${error.message}`);
      }

      return newItem as PurchaseOrderItem;
    } catch (error) {
      console.error('Error creating purchase order item:', error);
      throw error;
    }
  },

  async update(id: string, data: PurchaseOrderItemUpdate): Promise<PurchaseOrderItem> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.component_id !== undefined) updateData.component_id = data.component_id;
      if (data.item_name !== undefined) updateData.item_name = data.item_name;
      if (data.item_sku !== undefined) updateData.item_sku = data.item_sku;
      if (data.vendor !== undefined) updateData.vendor = data.vendor;
      if (data.quantity !== undefined) updateData.quantity = data.quantity;
      if (data.unit_cost !== undefined) updateData.unit_cost = data.unit_cost;
      if (data.total_cost !== undefined) updateData.total_cost = data.total_cost;
      if (data.description !== undefined) updateData.description = data.description;

      const { data: updatedItem, error } = await supabase
        .from('purchase_order_items')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating purchase order item:', error);
        throw new Error(`Failed to update purchase order item: ${error.message}`);
      }

      if (!updatedItem) {
        throw new Error('Purchase order item not found');
      }

      return updatedItem as PurchaseOrderItem;
    } catch (error) {
      console.error('Error updating purchase order item:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('purchase_order_items').delete().eq('id', id);

      if (error) {
        console.error('Error deleting purchase order item:', error);
        throw new Error(`Failed to delete purchase order item: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting purchase order item:', error);
      throw error;
    }
  },

  async deleteByPurchaseOrderId(purchaseOrderId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('purchase_order_items')
        .delete()
        .eq('purchase_order_id', purchaseOrderId);

      if (error) {
        console.error('Error deleting purchase order items:', error);
        throw new Error(`Failed to delete purchase order items: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting purchase order items:', error);
      throw error;
    }
  },
};
